import { FastField, Field } from 'formik';
import React, { useMemo } from 'react';
import { ABAC, APITypes } from '../../../../../primary-record-types';
import { getIntl } from '../../../../../utils/intl';
import { required, email, composeValidators, consentCheck } from '../../../../../utils/validators';
import getName from '../../../../../utils/getName';
import { handleFormikSubmit } from '../../../../../utils/handleFormikSubmit';
import useRecordEvent from '../../../../../hooks/useRecordEvent';
import { CenteredFormRow, Form, SplitFormRow } from '../../../../v2BaseComponents/Form';
import { FormStack } from '../../../../v2BaseComponents/Stack';
import { FormikInputs } from '../../../../v2BaseComponents/Inputs';

const intl = getIntl('profile-invite-collaborator-form', {
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    submit: 'Save and Share',
    consent:
        'As a Profile Organizer or Collaborator, I, {adminName}, confirm I have the permission and right to share access to {profileName}’s Medical Profile to {inviteeName}.',
    message: 'Message',
    inviteCollaborator: 'Invite Collaborator',
    thisPerson: 'this person',
});

export type InviteFormValue = Omit<
    APITypes.medicalProfileService.MedicalProfileCollaboratorInviteBody,
    'careTeamMemberId'
> & {
    acceptConsent: boolean;
};

export type InviteCollaboratorOwnProps = {
    initialValues?: InviteFormValue;
    isDisabled?: boolean;
    adminName: string;
    profileName: string;
    onSubmit: (values: InviteFormValue) => void;
    isSubmitting?: boolean;
    saveText?: string;
    onCancel: () => void;
    mostPermissiveAppPermissions: ABAC.AppPermissions;
};

export type InviteCollaboratorProps = InviteCollaboratorOwnProps;
export const getFeatureToggledDefaultOptions = (): InviteFormValue => ({
    email: '',
    firstName: '',
    lastName: '',
    message: '',
    appPermissions: {
        idsAndInfo: 'view',
        medications: 'view',
        conditions: 'view',
        allergies: 'view',
        careTeam: 'view',
        visits: 'view',
        patientPortals: 'view',
        cloudCalendars: 'view',
        settings: 'view',
        reports: 'view',
        files: 'view',
        imaging: 'view',
        procedures: 'view',
        vaccinations: 'view',
        labs: 'view',
        collaborators: 'view',
    },
    acceptConsent: false,
});

const InviteCollaborator: React.FC<InviteCollaboratorProps> = ({
    isDisabled,
    initialValues,
    onSubmit,
    isSubmitting,
    adminName,
    profileName,
    saveText,
    onCancel,
    mostPermissiveAppPermissions,
}) => {
    const defaultInitialValues = useMemo<InviteFormValue>(() => {
        return getFeatureToggledDefaultOptions();
    }, []);

    const recordEvent = useRecordEvent();

    return (
        <Form
            actionContainerSubmit
            initialValues={initialValues || defaultInitialValues}
            saveText={saveText}
            onCancel={onCancel}
            cancelButtonVariant="secondary"
            onSubmit={(values, helpers) => {
                onSubmit(values);
                handleFormikSubmit(values, helpers);
                recordEvent('inviteCollaborator');
            }}
        >
            {({ values }) => {
                return (
                    <FormStack>
                        <SplitFormRow>
                            <FastField
                                component={FormikInputs.Input}
                                name="firstName"
                                isRequired
                                title={intl('firstName')}
                                isDisabled={isDisabled || isSubmitting}
                                validate={required}
                            />
                            <FastField
                                component={FormikInputs.Input}
                                name="lastName"
                                isRequired
                                title={intl('lastName')}
                                isDisabled={isDisabled || isSubmitting}
                                validate={required}
                            />
                        </SplitFormRow>
                        <SplitFormRow>
                            <FastField
                                component={FormikInputs.Input}
                                name="email"
                                isRequired
                                title={intl('email')}
                                isDisabled={isDisabled || isSubmitting}
                                validate={composeValidators(required, email)}
                                autoCapitalize="none"
                            />
                            <FastField
                                component={FormikInputs.TextArea}
                                name="message"
                                isRequired
                                title={intl('message')}
                                isDisabled={isDisabled || isSubmitting}
                            />
                        </SplitFormRow>
                        <CenteredFormRow>
                            <FastField
                                component={FormikInputs.PermissionsInput}
                                name="appPermissions"
                                isDisabled={isDisabled || isSubmitting}
                                showEditCollaboratorWarning
                                mostPermissiveAppPermissions={mostPermissiveAppPermissions}
                                initialAppPermissions={initialValues || defaultInitialValues}
                            />
                        </CenteredFormRow>
                        <CenteredFormRow>
                            <Field
                                name="acceptConsent"
                                isDisabled={isDisabled || isSubmitting}
                                validate={consentCheck}
                                component={FormikInputs.Checkbox}
                                title={intl('consent', {
                                    adminName,
                                    profileName,
                                    inviteeName:
                                        values.firstName && values.lastName
                                            ? getName({ firstName: values.firstName, lastName: values.lastName })
                                            : intl('thisPerson'),
                                })}
                                topAlignCheckbox
                            />
                        </CenteredFormRow>
                    </FormStack>
                );
            }}
        </Form>
    );
};

export default React.memo(InviteCollaborator);
